// Core Imports
import type { TResponse } from '@/core/types/axios'
import type { TAdminListParams } from '@/core/lib/table-query'
import { queryStaticList } from '@/core/lib/static-list'

// Module Imports
import {
  TEAM_ACHIEVEMENTS_SEED,
  teamsForAchievement,
} from '@/modules/team-achievement/team-achievement.data'
import type {
  TCreateTeamAchievementRequest,
  TEditTeamAchievementRequest,
  TTeamAchievement,
  TTeamAchievementDetail,
  TToggleTeamAchievementActiveRequest,
} from '@/modules/team-achievement/team-achievement.types'

// In-memory store (clone of the seed) so create / edit / toggle / delete feel
// live in the UI until the real endpoints land. Swap the bodies below for
// `api.*` calls against `teamAchievementEndpoints` — signatures already match.
let store: TTeamAchievement[] = TEAM_ACHIEVEMENTS_SEED.map((a) => ({ ...a }))

let seq = 0
const nextId = () => {
  seq += 1
  return `custom-${seq}`
}

export async function getTeamAchievementList(
  params: TAdminListParams
): Promise<TResponse<TTeamAchievement[]>> {
  return queryStaticList(store, params)
}

export async function getTeamAchievementDetail(
  id: string
): Promise<TTeamAchievementDetail> {
  const found = store.find((a) => a.id === id)
  if (!found) throw new Error('TEAM_ACHIEVEMENT_NOT_FOUND')
  return { ...found, teams: teamsForAchievement(id) }
}

export async function createTeamAchievement(
  payload: TCreateTeamAchievementRequest
): Promise<void> {
  const image = URL.createObjectURL(payload.file)
  store = [
    {
      id: nextId(),
      name: payload.name,
      description: payload.description,
      image,
      tier: 'gold',
      unlock_rules: payload.unlock_rules,
      acquired_at: null,
      is_active: true,
    },
    ...store,
  ]
}

export async function editTeamAchievement(
  payload: TEditTeamAchievementRequest
): Promise<void> {
  store = store.map((a) =>
    a.id === payload.id
      ? {
          ...a,
          name: payload.name ?? a.name,
          description: payload.description ?? a.description,
          image: payload.file ? URL.createObjectURL(payload.file) : a.image,
          unlock_rules: payload.unlock_rules ?? a.unlock_rules,
        }
      : a
  )
}

export async function toggleTeamAchievementActive(
  payload: TToggleTeamAchievementActiveRequest
): Promise<TTeamAchievement> {
  store = store.map((a) =>
    a.id === payload.id ? { ...a, is_active: payload.is_active } : a
  )
  const updated = store.find((a) => a.id === payload.id)
  if (!updated) throw new Error('TEAM_ACHIEVEMENT_NOT_FOUND')
  return updated
}

export async function deleteTeamAchievement(id: string): Promise<void> {
  store = store.filter((a) => a.id !== id)
}
